/**
 * Standalone Telemetry SDK for Cognior Admin & modular client integrations.
 * Exposes core tracking lifecycle hooks with local queueing and automatic
 * batch flushing and retry capabilities.
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ENCODING_LEN = 32;

const storage = {
  getItem: (key) => {
    try {
      return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, value);
        return true;
      }
    } catch (e) {
      return false;
    }
    return false;
  }
};

const isSystemOrgId = (id) => {
  if (!id) return false;
  const str = String(id).trim().toLowerCase();
  return str === '00000000-0000-0000-0000-000000000001' || str === '00000000-0000-0000-0000-000000000000';
};

/**
 * Generic telemetry rejects these events; they must be dispatched to dedicated backend endpoints.
 */
const BLOCKED_GENERIC_HOOKS = new Set(['customer_analytics.viewed']);

class TelemetrySdk {
  constructor() {
    this.isInitialized = false;
    this.apiBaseUrl = '';
    this.moduleKey = '';
    this.getOrgId = () => null;
    this.getUserId = () => null;
    this.getSiteCollectionId = () => null;
    this.getAuthToken = () => null;
    this.batchIntervalMs = 30000;
    this.dedupWindowMs = 1000;
    this._recentEvents = new Map();
    this.storageKey = '';
    this.sessionStorageKey = '';
    this.sessionId = '';
    this.debug = false;
    this.useStorage = true;
    this.inMemoryQueue = [];
    this.intervalId = null;
    this.isFlushing = false;
    this.lastFlushTime = 'Never';
    this.lastFlushStatus = 'None';
  }

  /**
   * Initialize the SDK with configuration options.
   * @param {Object} config Config properties
   */
  initialize(config = {}) {
    if (this.isInitialized) {
      if (this.debug) console.log('[TelemetrySDK] Already initialized');
      return;
    }

    this.apiBaseUrl = config.apiBaseUrl || '';
    this.moduleKey = config.moduleKey || 'iap_admin';
    this.getOrgId = config.getOrgId || (() => null);
    this.getUserId = config.getUserId || (() => null);
    this.getSiteCollectionId = config.getSiteCollectionId || (() => null);
    this.getAuthToken = typeof config.getAuthToken === 'function' ? config.getAuthToken : null;
    this.batchIntervalMs = typeof config.batchIntervalMs === 'number' ? config.batchIntervalMs : 30000;
    this.dedupWindowMs = typeof config.dedupWindowMs === 'number' ? config.dedupWindowMs : 1000;
    this.storageKey = config.storageKey || 'telemetry_event_queue';
    this.sessionStorageKey = config.sessionStorageKey || 'telemetry_session_id';
    this.debug = !!config.debug;
    this.useStorage = config.useStorage !== false;

    // Resolve or generate session ID
    this.sessionId = this._resolveSession();

    // Start background flusher loop
    this._startInterval();

    // Register unload handler
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this._flushOnUnload();
      });
    }

    this.isInitialized = true;

    if (this.debug) {
      console.log('[TelemetrySDK] Initialized successfully:', {
        apiBaseUrl: this.apiBaseUrl,
        moduleKey: this.moduleKey,
        batchIntervalMs: this.batchIntervalMs,
        sessionId: this.sessionId,
        storageKey: this.storageKey
      });
    }
  }

  _getAuthToken() {
    if (typeof this.getAuthToken === 'function') {
      return this.getAuthToken();
    }
    return null;
  }

  _isLoggedOut() {
    if (typeof this.getAuthToken === 'function') {
      const token = this.getAuthToken();
      return !token;
    }
    return false;
  }

  /**
   * Tracks a telemetry event.
   * @param {string} eventName Name of the event (e.g. 'feature.used')
   * @param {Object} payload Custom properties of the event
   */
  track(eventName, payload = {}) {
    if (BLOCKED_GENERIC_HOOKS.has(eventName)) {
      if (this.debug) {
        console.warn(`[TelemetrySDK] Event '${eventName}' is rejected by generic telemetry. Use the dedicated typed endpoint instead.`);
      }
      return;
    }

    if (!this.isInitialized) {
      if (this.debug) {
        console.warn('[TelemetrySDK] track() called before initialization. Initializing with defaults...');
      }
      this.initialize();
    }

    if (this._isLoggedOut()) {
      if (this.debug) {
        console.log('[TelemetrySDK] Skipping event tracking: User is logged out (no auth token).');
      }
      return;
    }

    const timestamp = Date.now();

    // Resolve details dynamically
    const featureKey = payload.featureKey || payload.dimensions?.featureKey || '';
    const orgId = this.getOrgId() || payload.organizationId || null;
    const userId = this.getUserId() || payload.userId || null;
    const siteCollectionId = this.getSiteCollectionId() || payload.siteCollectionId || null;

    // Deduplication check: Suppress identical events triggered within dedupWindowMs (default 1000ms)
    if (this.dedupWindowMs > 0) {
      const dedupKey = `${eventName}|${featureKey}|${siteCollectionId || ''}|${orgId || ''}`;
      const lastTime = this._recentEvents.get(dedupKey);
      if (lastTime && (timestamp - lastTime < this.dedupWindowMs)) {
        if (this.debug) {
          console.log(`[TelemetrySDK] Duplicate event suppressed (within ${this.dedupWindowMs}ms): ${dedupKey}`);
        }
        return;
      }
      this._recentEvents.set(dedupKey, timestamp);

      // Prune old entries if map grows
      if (this._recentEvents.size > 50) {
        for (const [key, time] of this._recentEvents.entries()) {
          if (timestamp - time > this.dedupWindowMs) {
            this._recentEvents.delete(key);
          }
        }
      }
    }

    const occurredAtUtc = new Date(timestamp).toISOString();

    // Generate sanitized name for the eventId prefix
    const sanitizedEventName = eventName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const ulid = this._generateUlid(timestamp);
    
    // Unique eventId pattern matching standard evt_<moduleKey>_<eventName>_<ULID>
    const eventId = `evt_${this.moduleKey}_${sanitizedEventName}_${ulid}`;

    // Dimensions compilation
    const rawDimensions = {
      billingDimension: payload.dimensions?.billingDimension || 'FeatureUsage',
      featureKey: featureKey,
      pageUrl: payload.dimensions?.pageUrl || (typeof window !== 'undefined' ? window.location.pathname : ''),
      host: payload.dimensions?.host || (typeof window !== 'undefined' ? window.location.host : ''),
      ...payload.dimensions
    };

    const dimensions = {};
    for (const key of Object.keys(rawDimensions)) {
      const val = rawDimensions[key];
      if (val !== null && val !== undefined) {
        if (Array.isArray(val)) {
          dimensions[key] = val.join(', ');
        } else if (typeof val === 'object') {
          dimensions[key] = JSON.stringify(val);
        } else {
          dimensions[key] = String(val);
        }
      }
    }

    const event = {
      eventId,
      moduleKey: this.moduleKey,
      eventName,
      featureKey: payload.featureKey || dimensions.featureKey || '',
      sessionId: this.sessionId,
      userId,
      siteCollectionId,
      quantity: typeof payload.quantity === 'number' ? payload.quantity : 1,
      unit: payload.unit || 'count',
      occurredAtUtc,
      classification: payload.classification || 'Billable',
      dimensions
    };

    const queueRecord = {
      id: eventId,
      orgId,
      event,
      retryCount: 0,
      queuedAt: timestamp
    };

    const queue = this.getQueue();
    queue.push(queueRecord);
    this.saveQueue(queue);

    if (this.debug) {
      console.log(`[TelemetrySDK] Queued event: ${eventName}`, event);
    }

    if (queue.length >= 20) {
      if (this.debug) console.log(`[TelemetrySDK] Queue reached 20 events. Triggering immediate flush.`);
      this.flush();
    }
  }

  /**
   * Flush the current queue by sending batches to the server.
   */
  async flush() {
    if (!this.isInitialized) return;
    if (this.isFlushing) {
      if (this.debug) console.log('[TelemetrySDK] Flush already in progress, skipping.');
      return;
    }

    if (this._isLoggedOut()) {
      if (this.debug) console.log('[TelemetrySDK] User is logged out (no auth token). Skipping flush.');
      return;
    }

    const queue = this.getQueue();
    if (queue.length === 0) {
      if (this.debug) console.log('[TelemetrySDK] Queue empty, skipping flush.');
      return;
    }

    this.isFlushing = true;
    if (this.debug) console.log(`[TelemetrySDK] Flushing ${queue.length} events...`);

    const groups = {};
    const unresolvableRecords = [];

    // Group events by organizationId
    for (const record of queue) {
      if (record.event && record.event.dimensions) {
        const sanitized = {};
        for (const key of Object.keys(record.event.dimensions)) {
          const val = record.event.dimensions[key];
          if (val !== null && val !== undefined) {
            if (Array.isArray(val)) {
              sanitized[key] = val.join(', ');
            } else if (typeof val === 'object') {
              sanitized[key] = JSON.stringify(val);
            } else {
              sanitized[key] = String(val);
            }
          }
        }
        record.event.dimensions = sanitized;
      }

      let orgId = record.orgId;
      if (!orgId) {
        orgId = this.getOrgId();
        if (orgId) {
          record.orgId = orgId;
          record.event.userId = record.event.userId || this.getUserId();
          record.event.siteCollectionId = record.event.siteCollectionId || this.getSiteCollectionId();
        }
      }

      if (isSystemOrgId(orgId)) {
        if (this.debug) {
          console.log(`[TelemetrySDK] Bypassing telemetry event post for System Organization ID: ${orgId}`);
        }
        continue;
      }

      if (!orgId) {
        // Keep in queue until organization context is resolved (e.g. user logs in)
        unresolvableRecords.push(record);
        continue;
      }

      // Group by orgId and then by batchRequestId (or 'new' for unassigned)
      const batchId = record.batchRequestId || 'new';
      
      if (!groups[orgId]) {
        groups[orgId] = {};
      }
      if (!groups[orgId][batchId]) {
        groups[orgId][batchId] = [];
      }
      groups[orgId][batchId].push(record);
    }

    const remainingQueue = [...unresolvableRecords];

    let hasSuccess = false;
    let hasFail = false;

    // Process each organization and batch group
    const sendPromises = [];
    Object.entries(groups).forEach(([orgId, batchGroups]) => {
      Object.entries(batchGroups).forEach(([batchId, records]) => {
        let requestId = batchId;
        if (requestId === 'new') {
          const timestamp = Date.now();
          requestId = `req_${this.moduleKey}_${this._generateUlid(timestamp)}`;
          // Assign to records so it's reused if requeued
          records.forEach(r => r.batchRequestId = requestId);
        }

        const payload = {
          requestId,
          events: records.map(r => r.event)
        };

        const base = this.apiBaseUrl.endsWith('/') ? this.apiBaseUrl.slice(0, -1) : this.apiBaseUrl;
        const url = `${base}/telemetry/organizations/${encodeURIComponent(orgId)}/events`;

        const promise = (async () => {
          try {
            const token = this._getAuthToken();
            const headers = {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            };

        if (this.debug) {
          console.log(`[TelemetrySDK] POST to ${url} for requestId: ${requestId}`);
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          if (this.debug) {
            console.log(`[TelemetrySDK] Batch ${requestId} sent successfully.`);
          }
          hasSuccess = true;
          // Events successfully processed, omitted from remainingQueue
        } else if (response.status === 401 || response.status === 403) {
          console.warn(`[TelemetrySDK] Batch ${requestId} failed: Server returned ${response.status} (Unauthorized/Forbidden). Dropping events for logged out/unauthorized session.`);
          hasFail = true;
          // Do not requeue records on 401/403
        } else if (response.status === 400) {
          console.error(`[TelemetrySDK] Batch ${requestId} failed: Server returned 400 (Bad Request). Dropping malformed payload.`);
          hasFail = true;
          // Do not requeue records on 400 Bad Request
        } else {
          console.warn(`[TelemetrySDK] Batch ${requestId} failed: Server returned ${response.status}`);
          hasFail = true;
          this._requeueRecords(records, remainingQueue);
        }
          } catch (err) {
            console.error(`[TelemetrySDK] Batch ${requestId} failed: Network or client error`, err);
            hasFail = true;
            this._requeueRecords(records, remainingQueue);
          }
        })();
        sendPromises.push(promise);
      });
    });

    await Promise.all(sendPromises);

    // Persist remaining items
    remainingQueue.sort((a, b) => a.queuedAt - b.queuedAt);
    this.saveQueue(remainingQueue);

    this.lastFlushTime = new Date().toLocaleTimeString();
    if (hasSuccess && !hasFail) {
      this.lastFlushStatus = 'Success';
    } else if (hasSuccess && hasFail) {
      this.lastFlushStatus = 'Partial Failure';
    } else if (!hasSuccess && hasFail) {
      this.lastFlushStatus = 'Failed';
    } else {
      this.lastFlushStatus = 'None';
    }

    this.isFlushing = false;
    if (this.debug) {
      console.log(`[TelemetrySDK] Flush iteration complete. Remaining in queue: ${remainingQueue.length}`);
    }
  }

  /**
   * Synchronous flush designed for page unload scenarios.
   * Uses fetch with keepalive to ensure delivery after the page closes.
   */
  _flushOnUnload() {
    if (!this.isInitialized || this._isLoggedOut()) return;
    const queue = this.getQueue();
    if (queue.length === 0) return;

    const groups = {};
    for (const record of queue) {
      let orgId = record.orgId || this.getOrgId();
      if (!orgId || isSystemOrgId(orgId)) continue;
      
      const batchId = record.batchRequestId || 'new';
      if (!groups[orgId]) groups[orgId] = {};
      if (!groups[orgId][batchId]) groups[orgId][batchId] = [];
      groups[orgId][batchId].push(record);
    }

    Object.entries(groups).forEach(([orgId, batchGroups]) => {
      Object.entries(batchGroups).forEach(([batchId, records]) => {
        let requestId = batchId;
        if (requestId === 'new') {
          requestId = `req_${this.moduleKey}_${this._generateUlid(Date.now())}`;
        }

        const payload = {
          requestId,
          events: records.map(r => r.event)
        };

        const base = this.apiBaseUrl.endsWith('/') ? this.apiBaseUrl.slice(0, -1) : this.apiBaseUrl;
        const url = `${base}/telemetry/organizations/${encodeURIComponent(orgId)}/events`;
        const token = this._getAuthToken();

        try {
          // Use fetch with keepalive for unload delivery
          if (typeof fetch !== 'undefined') {
            fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              },
              body: JSON.stringify(payload),
              keepalive: true
            }).catch(() => {});
          } else if (typeof window !== 'undefined' && window.navigator && window.navigator.sendBeacon) {
             // Fallback to sendBeacon if fetch with keepalive is unsupported (won't include auth headers though)
             const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
             window.navigator.sendBeacon(url, blob);
          }
        } catch (e) {
          // ignore on unload
        }
      });
    });

    // Clear queue to prevent resending on next session if keepalive succeeded
    // (A more robust implementation would rely on the server to deduplicate, which we support via requestId)
    this.saveQueue([]);
  }

  /**
   * Triggers clean shutdown of the SDK flush timers.
   */
  shutdown() {
    this._stopInterval();
    if (this.debug) console.log('[TelemetrySDK] Shutdown requested, executing final flush.');
    this.flush();
    this._recentEvents.clear();
    this.isInitialized = false;
  }

  /**
   * Read raw event queue.
   */
  getQueue() {
    if (this.inMemoryQueue && this.inMemoryQueue.length > 0) {
      return this.inMemoryQueue;
    }
    if (this.useStorage) {
      const raw = storage.getItem(this.storageKey);
      if (raw) {
        try {
          this.inMemoryQueue = JSON.parse(raw);
          return this.inMemoryQueue;
        } catch (e) {
          this.inMemoryQueue = [];
          return [];
        }
      }
    }
    return this.inMemoryQueue || [];
  }

  /**
   * Persist event queue.
   */
  saveQueue(queue) {
    this.inMemoryQueue = queue;
    if (this.useStorage) {
      setTimeout(() => {
        storage.setItem(this.storageKey, JSON.stringify(queue));
      }, 0);
    }
  }

  _requeueRecords(records, targetQueue) {
    records.forEach(r => {
      if (isSystemOrgId(r.orgId)) {
        if (this.debug) {
          console.warn(`[TelemetrySDK] Dropping event ${r.id} for System Organization ID ${r.orgId}.`);
        }
        return;
      }
      r.retryCount += 1;
      // Max 3 retries before dropping a persistently bad event
      if (r.retryCount <= 3) {
        targetQueue.push(r);
      } else if (this.debug) {
        console.warn(`[TelemetrySDK] Dropping event ${r.id} after exceeding max retries.`);
      }
    });
  }

  _resolveSession() {
    let sessId = null;
    try {
      if (typeof window !== 'undefined') {
        sessId = window.sessionStorage.getItem(this.sessionStorageKey);
      }
    } catch (e) {
      // ignore
    }

    if (!sessId) {
      const rand = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
      sessId = `sess_admin_${rand}`;
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(this.sessionStorageKey, sessId);
        }
      } catch (e) {
        // ignore
      }
    }
    return sessId;
  }

  _startInterval() {
    this._stopInterval();
    this.intervalId = setInterval(() => {
      this.flush();
    }, this.batchIntervalMs);
  }

  _stopInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  _generateUlid(timestamp) {
    let timePart = '';
    let tempTime = timestamp;
    for (let i = 0; i < 10; i++) {
      const mod = tempTime % ENCODING_LEN;
      timePart = ENCODING.charAt(mod) + timePart;
      tempTime = Math.floor(tempTime / ENCODING_LEN);
    }

    let randomPart = '';
    for (let i = 0; i < 16; i++) {
      const rand = Math.floor(Math.random() * ENCODING_LEN);
      randomPart += ENCODING.charAt(rand);
    }

    return timePart + randomPart;
  }
}

export default TelemetrySdk;
